import { Icon, Palette, RouteTabWithContent, TabsWithContent } from "@homebound/beam";
import { BidContractPlanPricingPage } from "src/components/bid-contracts/BidContractPlanPricingPage";
import { BidContractPlanPricingV2Page } from "src/components/bid-contracts/BidContractPlanPricingPageV2";
import { BidContractPurchaseOrdersPage } from "src/components/bid-contracts/BidContractPurchaseOrdersPage";
import { BidContractUnitPricingPage } from "src/components/bid-contracts/BidContractUnitPricingPage";
import { BidContractType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { BidContractRevisionFragment } from "src/routes/developments/procurement/DevelopmentContractPage.generated";
import { projectPaths } from "src/routes/routesDef";
import {
  createProjectBidContractOverviewUrl,
  createProjectBidContractPlanPricingUrl,
  createProjectBidContractPurchaseOrdersUrl,
  createProjectBidContractUnitPricingUrl,
} from "src/RouteUrls";
import { ProjectBidContractOverviewPage } from "./ProjectBidContractOverviewPage";

type ContractTabsProp = { selectedRevision: BidContractRevisionFragment };

export function ProjectBidContractTabs({ selectedRevision }: ContractTabsProp) {
  const { projectId } = useRouteParams<{ projectId: string }>();
  const showPlanPricing = selectedRevision.bidContract.type.code !== BidContractType.UnitBased;
  const showUnitPricing =
    selectedRevision.bidContract.type.code === BidContractType.UnitBased ||
    selectedRevision.bidContract.type.code === BidContractType.LegacyUnitAndTemplateBased;
  const isPlanBasedV2 = selectedRevision.bidPackageVersion?.bidPackage.isPlanBasedV2 ?? false;
  const tabs: RouteTabWithContent[] = [
    {
      href: createProjectBidContractOverviewUrl(
        projectId ?? selectedRevision.bidContract.projects[0].id,
        selectedRevision.id,
      ),
      path: projectPaths.bidContractOverview,
      name: "Overview",
      render: () => <ProjectBidContractOverviewPage />,
    },
    ...(showPlanPricing
      ? [
          {
            href: createProjectBidContractPlanPricingUrl(
              projectId ?? selectedRevision.bidContract.projects[0].id,
              selectedRevision.id,
            ),
            path: projectPaths.bidContractPlanPricing,
            name: "Plan-based Pricing",
            ...(selectedRevision.hasOutdatedScope
              ? { endAdornment: <Icon icon="errorCircle" inc={2} color={Palette.Gray900} /> }
              : {}),
            render: () => (isPlanBasedV2 ? <BidContractPlanPricingV2Page /> : <BidContractPlanPricingPage />),
          },
        ]
      : []),
    ...(showUnitPricing
      ? [
          {
            href: createProjectBidContractUnitPricingUrl(
              projectId ?? selectedRevision.bidContract.projects[0].id,
              selectedRevision.id,
            ),
            path: projectPaths.bidContractUnitPricing,
            name: "Unit Pricing",
            render: () => <BidContractUnitPricingPage />,
          },
        ]
      : []),
    ...(!selectedRevision.bidContract.isInternalEstimate
      ? [
          {
            href: createProjectBidContractPurchaseOrdersUrl(
              projectId ?? selectedRevision.bidContract.projects[0].id,
              selectedRevision.id,
            ),
            path: projectPaths.bidContractPurchaseOrders,
            name: "Purchase Orders",
            render: () => <BidContractPurchaseOrdersPage />,
          },
        ]
      : []),
  ];

  return <TabsWithContent tabs={tabs} />;
}
